import { useState } from 'react';
import { Button } from '@launchpad-ui/components';

import { Card, ErrorMessage, PageHeader, StatCard } from '../components/ui';
import { useFeedbackResults } from '../lib/hooks/useFeedback';
import type { ReviewRecord, RubricKey } from '../lib/types';

import styles from '../styles/common.module.css';

const RUBRIC_LABELS: Record<RubricKey, string> = {
  voice_tone: 'Voice & Tone',
  grammar: 'Grammar',
  empathy: 'Empathy',
  opening: 'Opening',
  closing: 'Closing',
  collect_data: 'Collect Data',
  troubleshooting: 'Troubleshooting',
  provide_why: 'Provide Why',
  efficiency: 'Efficiency',
  resolution: 'Resolution',
  slo: 'SLO',
  escalation: 'Escalation',
  zendesk: 'Zendesk',
  document: 'Documentation',
};

function ScoreBadge({ value }: { value: number | string }) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(n)) return null;
  const cls = n >= 80 ? styles.badgeCompleted : n >= 60 ? styles.badgePending : styles.badgeSkipped;
  return (
    <span className={`${styles.badge} ${cls}`} style={{ fontSize: '0.85rem' }}>
      {Math.round(n)}%
    </span>
  );
}

function ReviewCard({ review, index }: { review: ReviewRecord; index: number }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card>
      <div className={styles.spaceBetween}>
        <div>
          <div
            style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: 'var(--lp-color-text-ui-secondary)',
              textTransform: 'uppercase',
            }}
          >
            Review {index + 1}
            {review.ticket_number && ` · Ticket #${review.ticket_number}`}
            {review.timestamp && ` · ${new Date(review.timestamp).toLocaleDateString()}`}
          </div>
          {review.topic && <p style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}>{review.topic}</p>}
        </div>
        <ScoreBadge value={review.score} />
      </div>

      {(review.positive_feedback || review.constructive_feedback) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
            gap: '0.75rem',
            marginTop: '0.75rem',
          }}
        >
          {review.positive_feedback && (
            <div
              style={{
                backgroundColor: 'var(--lp-color-bg-feedback-success, #d1fae5)',
                padding: '0.75rem',
                borderRadius: 6,
              }}
            >
              <p
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  margin: '0 0 0.25rem 0',
                }}
              >
                Strengths
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>{review.positive_feedback}</p>
            </div>
          )}
          {review.constructive_feedback && (
            <div
              style={{
                backgroundColor: 'var(--lp-color-bg-feedback-warning, #fef3c7)',
                padding: '0.75rem',
                borderRadius: 6,
              }}
            >
              <p
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  margin: '0 0 0.25rem 0',
                }}
              >
                Areas to Improve
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>{review.constructive_feedback}</p>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: '0.75rem' }}>
        <Button size="small" variant="minimal" onPress={() => setExpanded((e) => !e)}>
          {expanded ? 'Hide rubric breakdown' : 'Show rubric breakdown'}
        </Button>
        {expanded && (
          <div
            style={{
              marginTop: '0.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
              gap: '0.25rem',
            }}
          >
            {(Object.keys(RUBRIC_LABELS) as RubricKey[]).map((key) => {
              const value = review[key];
              if (value === '' || value == null) return null;
              return (
                <div key={key} style={{ fontSize: '0.75rem' }}>
                  <span style={{ color: 'var(--lp-color-text-ui-secondary)' }}>{RUBRIC_LABELS[key]}:</span>{' '}
                  <span style={{ fontWeight: 500 }}>{value}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}

export function Feedback() {
  const resultsQuery = useFeedbackResults();

  if (resultsQuery.isPending) {
    return <div className={styles.pageNarrow}>Loading…</div>;
  }

  if (resultsQuery.isError) {
    return (
      <div className={styles.pageNarrow}>
        <ErrorMessage message={(resultsQuery.error as Error).message || 'Failed to load feedback'} />
      </div>
    );
  }

  const tse = resultsQuery.data?.tse ?? '';
  const reviews = resultsQuery.data?.reviews ?? [];

  const avg = reviews.length
    ? Math.round(
        reviews.reduce((s, r) => s + (typeof r.score === 'number' ? r.score : parseFloat(String(r.score)) || 0), 0) /
          reviews.length,
      )
    : null;
  const latest = reviews.at(-1);
  const latestScore =
    latest && typeof latest.score === 'number' ? latest.score : latest ? parseFloat(String(latest.score)) || 0 : 0;

  return (
    <div className={styles.pageNarrow}>
      <PageHeader title="QA Feedback" subtitle={tse} />

      <div className={styles.statGrid}>
        <StatCard label="Reviews Completed" value={reviews.length} />
        {avg !== null && <StatCard label="Average Score" value={`${avg}%`} highlight />}
        {reviews.length > 0 && <StatCard label="Latest Score" value={`${Math.round(latestScore)}%`} />}
      </div>

      {reviews.length === 0 ? (
        <div className={styles.empty}>No reviews on file yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {reviews.map((r, i) => (
            <ReviewCard key={i} review={r} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
